import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import LineEditIcon from 'LuxKit/Icons/line/LineEditIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import PastPassengerNumberModal, { hasPastPassengerDetails } from './PastPassengerNumberModal'

interface Props {
  itemId: number
  passengerCount: number
  pastPassengerNumbers?: Array<string | null>
  passengersInfo?: Array<App.PastPassengerInfo>
  pastPassengerFieldsRequired: Array<string>
  personTitlesSupported: Array<App.Cruises.BookingPersonTitles>
  onSave: (passengerIndex: number, details: {
    pastPassengerNumber: string
    passengerInfo: App.PastPassengerInfo
  }) => void
  onRemove: (passengerIndex: number) => void
}

const PassengerNumber = styled(BodyText)`
  flex: 1;
  min-width: 0;
`

function PastPassengerNumberPicker({
  itemId,
  passengerCount,
  pastPassengerNumbers = [],
  passengersInfo = [],
  pastPassengerFieldsRequired,
  personTitlesSupported,
  onSave,
  onRemove,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const openPassengerModal = useCallback((passengerIndex: number) => {
    showModal(
      <PastPassengerNumberModal
        itemId={itemId}
        passengerIndex={passengerIndex}
        pastPassengerNumber={pastPassengerNumbers[passengerIndex]}
        passengerInfo={passengersInfo[passengerIndex]}
        pastPassengerFieldsRequired={pastPassengerFieldsRequired}
        personTitlesSupported={personTitlesSupported}
        onSave={(details) => onSave(passengerIndex, details)}
      />,
    )
  }, [itemId, onSave, pastPassengerFieldsRequired, pastPassengerNumbers, passengersInfo, personTitlesSupported, showModal])

  return <Group direction="vertical" gap={12}>
    {Array.from({ length: passengerCount }, (_, passengerIndex) => {
      const pastPassengerNumber = pastPassengerNumbers[passengerIndex]
      const passengerInfo = passengersInfo[passengerIndex]
      const hasDetails = hasPastPassengerDetails(
        pastPassengerNumber,
        passengerInfo,
        pastPassengerFieldsRequired,
      )
      const passengerLabel = intl.formatMessage({
        id: 'mng6a4SRJVUCcooo9uce',
        defaultMessage: 'Passenger {number}',
        description: 'Passenger label in the cruise past passenger number picker',
      }, { number: passengerIndex + 1 })

      if (!hasDetails) {
        return <Group
          key={passengerIndex}
          direction="horizontal"
          wrap="wrap"
          verticalAlign="center"
          gap={8}
        >
          <BodyText variant="medium" weight="bold">{passengerLabel}</BodyText>
          <TextButton
            kind="tertiary"
            size="small"
            onClick={() => openPassengerModal(passengerIndex)}
            data-testid={`occupancy-item-${itemId}-inform-past-passenger-number-${passengerIndex}`}
          >
            {intl.formatMessage({
              id: 'dj39Em4w4OnGTzyxWqqY',
              defaultMessage: 'Inform passenger number',
              description: 'Button to add a past passenger number for a cruise passenger',
            })}
          </TextButton>
        </Group>
      }

      return <Group
        key={passengerIndex}
        direction="horizontal"
        wrap="wrap"
        verticalAlign="center"
        gap={8}
      >
        <BodyText variant="medium" weight="bold">{passengerLabel}</BodyText>
        <PassengerNumber variant="medium">
          {intl.formatMessage({
            id: 'rK4PyohiXUK91L9v_tr-',
            defaultMessage: 'Passenger number: {number}',
            description: 'Saved cruise past passenger number shown for a passenger',
          }, { number: pastPassengerNumber })}
        </PassengerNumber>
        <IconButton
          kind="tertiary"
          size="small"
          aria-label={intl.formatMessage({
            id: 'Isyi7b3q88rF-XdCP-6g',
            defaultMessage: 'Edit passenger {number} past passenger number',
            description: 'Accessible label for editing a cruise passenger past passenger number',
          }, { number: passengerIndex + 1 })}
          onClick={() => openPassengerModal(passengerIndex)}
        >
          <LineEditIcon />
        </IconButton>
        <IconButton
          kind="tertiary"
          size="small"
          aria-label={intl.formatMessage({
            id: 'Ezl9SLLboowplla_x2H0',
            defaultMessage: 'Remove passenger {number} past passenger number',
            description: 'Accessible label for removing a cruise passenger past passenger number',
          }, { number: passengerIndex + 1 })}
          onClick={() => onRemove(passengerIndex)}
        >
          <LineTrashAltIcon />
        </IconButton>
      </Group>
    })}
  </Group>
}

export default PastPassengerNumberPicker
